import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// ~16 s animated product promo for the About page: six short scenes + an outro, looping.
// Pure React/framer-motion (no video file), so it is translated, sharp at any size and weighs nothing.
// `capture` renders it alone at full size and starts at once (used to record an MP4 of it).

const SCENE_MS = 2500;
const OUTRO_MS = 1800;
const C = {
  bg: '#0a0a0f', card: '#111118', elevated: '#1a1a24', border: 'rgba(255,255,255,0.08)',
  text: '#e8e8ed', sub: '#8b8b9e', muted: 'var(--text-muted)', // the panel's muted grey (the old #4a4a5a was 2.2:1)
  blue: '#60a5fa', green: '#22c55e', red: '#f43f5e', amber: '#f59e0b', cyan: '#22d3ee', violet: '#a78bfa', orange: '#f97316',
};
const MAPS = ['Mirage', 'Inferno', 'Nuke', 'Ancient', 'Anubis', 'Dust2', 'Train'];

const fade = { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, transition: { duration: 0.35 } };

const Box = ({ children, style, ...rest }) => (
  <motion.div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, ...style }} {...rest}>{children}</motion.div>
);

const Pill = ({ color, children, style, ...rest }) => (
  <motion.span
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.2em 0.7em', borderRadius: 999, fontSize: '0.72em', fontWeight: 700,
      background: `${color}1a`, border: `1px solid ${color}40`, color, whiteSpace: 'nowrap', ...style }}
    {...rest}
  >{children}</motion.span>
);

const Dot = ({ color }) => <span style={{ width: '0.5em', height: '0.5em', borderRadius: '50%', background: color, display: 'inline-block' }} />;

// Counts through `steps` while the scene is on screen.
function useSteps(count, every, delay = 0) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let i = 0; let iv;
    const start = setTimeout(() => { iv = setInterval(() => { i += 1; setN(i); if (i >= count) clearInterval(iv); }, every); }, delay);
    return () => { clearTimeout(start); clearInterval(iv); };
  }, [count, every, delay]);
  return n;
}

function SceneSetup() {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gap: '0.9em', width: '78%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '1em' }}>
        <Box initial={{ x: -60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.45 }} style={{ padding: '0.9em', textAlign: 'center' }}>
          <div style={{ fontWeight: 800, color: C.blue, fontSize: '1.1em' }}>Team Alpha</div>
          <div style={{ color: C.muted, fontSize: '0.7em', marginTop: 4 }}>5 {t('aboutPage.promo.players', 'players')}</div>
        </Box>
        <Pill color={C.amber} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: 'spring' }} style={{ fontSize: '0.9em' }}>BO3</Pill>
        <Box initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.45 }} style={{ padding: '0.9em', textAlign: 'center' }}>
          <div style={{ fontWeight: 800, color: C.orange, fontSize: '1.1em' }}>Team Bravo</div>
          <div style={{ color: C.muted, fontSize: '0.7em', marginTop: 4 }}>5 {t('aboutPage.promo.players', 'players')}</div>
        </Box>
      </div>
      <div style={{ display: 'flex', gap: '0.45em', flexWrap: 'wrap', justifyContent: 'center' }}>
        {MAPS.map((m, i) => (
          <Pill key={m} color={C.cyan} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + i * 0.08 }}>{m}</Pill>
        ))}
      </div>
      <Box initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} style={{ padding: '0.6em 0.9em', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8em' }}>
        <span style={{ color: C.sub }}>{t('aboutPage.promo.server', 'Server')} #1 · 27015</span>
        <span style={{ color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}><Dot color={C.green} /> {t('aboutPage.promo.online', 'Online')}</span>
      </Box>
    </div>
  );
}

// ban, ban, pick, pick, ban, ban → decider
const VETO = [['ban', 0], ['ban', 1], ['pick', 0], ['pick', 1], ['ban', 0], ['ban', 1]];
const VETO_ORDER = [2, 6, 0, 1, 4, 5];
function SceneVeto() {
  const { t } = useTranslation();
  const step = useSteps(VETO.length + 1, 280, 200);
  const state = {};
  VETO_ORDER.forEach((mapIdx, i) => { if (i < step && i < VETO.length) state[mapIdx] = VETO[i]; });
  const decider = step > VETO.length ? 3 : -1;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.6em', width: '78%' }}>
      {MAPS.map((m, i) => {
        const s = state[i];
        const color = i === decider ? C.amber : s ? (s[0] === 'ban' ? C.red : C.green) : C.border;
        const label = i === decider ? t('aboutPage.promo.decider', 'Decider') : s ? `${s[1] ? 'Bravo' : 'Alpha'} · ${s[0] === 'ban' ? t('aboutPage.promo.ban', 'Ban') : t('aboutPage.promo.pick', 'Pick')}` : '';
        return (
          <Box key={m} animate={{ borderColor: color, opacity: s && s[0] === 'ban' ? 0.45 : 1, scale: (s && s[0] === 'pick') || i === decider ? 1.04 : 1 }}
            style={{ padding: '0.8em 0.5em', textAlign: 'center', minHeight: '3.6em' }}>
            <div style={{ fontWeight: 800, color: C.text, textDecoration: s && s[0] === 'ban' ? 'line-through' : 'none' }}>{m}</div>
            <div style={{ fontSize: '0.62em', marginTop: 4, color, fontWeight: 700, minHeight: '1.2em' }}>{label}</div>
          </Box>
        );
      })}
    </div>
  );
}

const KILLS = [
  ['s1mple_', 'ak47', true, 'NiKo'], ['ropz', 'awp', false, 'm0NESY'], ['NiKo', 'deagle', true, 'broky'],
  ['donk', 'm4a1', true, 'ropz'],
];
function SceneLive() {
  const { t } = useTranslation();
  const n = useSteps(24, 70, 150);
  const a = Math.min(13, Math.round(n * 13 / 24));
  const b = Math.min(11, Math.round(n * 11 / 24));
  const k = useSteps(KILLS.length, 380, 300);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', width: '84%', alignItems: 'center' }}>
      <Box style={{ padding: '1em', textAlign: 'center' }}>
        <Pill color={C.green} animate={{ opacity: [1, 0.4, 1] }} transition={{ repeat: Infinity, duration: 1.2 }}><Dot color={C.green} /> LIVE</Pill>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: '2.6em', margin: '0.2em 0', color: C.text }}>
          <span style={{ color: C.blue }}>{a}</span><span style={{ color: C.muted }}> : </span><span style={{ color: C.orange }}>{b}</span>
        </div>
        <div style={{ color: C.sub, fontSize: '0.75em' }}>Mirage · {t('aboutPage.promo.round', 'Round')} {a + b + 1}</div>
      </Box>
      <div style={{ display: 'grid', gap: '0.4em' }}>
        {KILLS.slice(0, k).map(([killer, weapon, hs, victim], i) => (
          <Box key={i} initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
            style={{ padding: '0.45em 0.6em', display: 'flex', alignItems: 'center', gap: '0.5em', fontSize: '0.75em', fontWeight: 700 }}>
            <span style={{ color: i % 2 ? C.orange : C.blue }}>{killer}</span>
            <img src={`/killfeed/${weapon}.png`} alt="" style={{ height: '1.2em', filter: 'brightness(1.4)' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            {hs && <img src="/killfeed/headshot.png" alt="" style={{ height: '1.1em' }} />}
            <span style={{ color: i % 2 ? C.blue : C.orange, marginLeft: 'auto' }}>{victim}</span>
          </Box>
        ))}
      </div>
    </div>
  );
}

function SceneFix() {
  const { t } = useTranslation();
  const step = useSteps(4, 520, 150);
  const restored = step >= 2;
  return (
    <div style={{ display: 'grid', gap: '1em', width: '78%', justifyItems: 'center' }}>
      <AnimatePresence mode="wait">
        {step < 3
          ? <Pill key="p" color={C.amber} {...fade} style={{ fontSize: '0.9em' }}>⏸ {t('aboutPage.promo.techPause', 'Tech pause')}</Pill>
          : <Pill key="u" color={C.green} {...fade} style={{ fontSize: '0.9em' }}>▶ {t('aboutPage.promo.unpaused', 'Match resumed')}</Pill>}
      </AnimatePresence>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '0.35em', width: '100%' }}>
        {Array.from({ length: 12 }, (_, i) => {
          const r = i + 1;
          const gone = restored && r > 7;
          const target = step >= 1 && r === 7;
          return (
            <Box key={r} animate={{ opacity: gone ? 0.15 : 1, borderColor: target ? C.cyan : C.border, scale: target ? 1.12 : 1 }}
              style={{ padding: '0.5em 0', textAlign: 'center', fontSize: '0.72em', fontWeight: 700, color: target ? C.cyan : C.sub, background: i % 3 === 1 ? '#1a1220' : C.card }}>
              {r}
            </Box>
          );
        })}
      </div>
      <AnimatePresence>
        {restored && (
          <Box key="t" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ padding: '0.55em 1em', borderColor: `${C.green}55`, color: C.green, fontWeight: 700, fontSize: '0.85em' }}>
            ✓ {t('aboutPage.promo.restored', 'Round 7 restored from backup')}
          </Box>
        )}
      </AnimatePresence>
    </div>
  );
}

function SceneLan() {
  const { t } = useTranslation();
  const on = useSteps(3, 380, 500);
  const xs = [16.6, 50, 83.4];
  return (
    <div style={{ position: 'relative', width: '78%', height: '11em' }}>
      <svg viewBox="0 0 100 60" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {xs.map((x, i) => (
          <motion.line key={i} x1="50" y1="14" x2={x} y2="44" stroke={i < on ? C.green : C.muted} strokeWidth="0.5" strokeDasharray="1.5 1"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.2 + i * 0.15, duration: 0.5 }} />
        ))}
      </svg>
      <Box initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ position: 'absolute', left: '50%', top: 0, transform: 'translateX(-50%)', padding: '0.5em 1em', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
        <img src="/logo.png" alt="" style={{ width: '1.3em', height: '1.3em', borderRadius: 4 }} /> {t('aboutPage.promo.panel', 'Panel')}
      </Box>
      {xs.map((x, i) => (
        <Box key={i} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 * i }}
          style={{ position: 'absolute', left: `${x}%`, bottom: 0, transform: 'translateX(-50%)', padding: '0.5em 0.8em', textAlign: 'center', fontSize: '0.75em', minWidth: '7.5em',
            borderColor: i < on ? `${C.green}66` : C.border }}>
          <div style={{ fontWeight: 800, color: C.text }}>{t('aboutPage.promo.machine', 'Machine {{n}}', { n: i + 1 })}</div>
          <div style={{ color: i < on ? C.green : C.muted, marginTop: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5 }}>
            <Dot color={i < on ? C.green : C.muted} /> {i < on ? t('aboutPage.promo.connected', 'Connected') : '…'}
          </div>
        </Box>
      ))}
      <Pill color={C.cyan} initial={{ opacity: 0 }} animate={{ opacity: on >= 3 ? 1 : 0 }} style={{ position: 'absolute', left: '50%', top: '42%', transform: 'translateX(-50%)', background: C.bg }}>
        {t('aboutPage.promo.noPorts', 'No port forwarding')}
      </Pill>
    </div>
  );
}

function SceneShare() {
  const { t } = useTranslation();
  const chips = [
    [t('aboutPage.promo.publicPage', 'Public page'), C.green], ['Discord', C.violet], [t('aboutPage.promo.demo', 'Demo'), C.amber], ['API', C.blue],
  ];
  const team = (name, color, delay, win) => (
    <Box initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0, borderColor: win ? `${C.green}88` : C.border }} transition={{ delay }}
      style={{ padding: '0.35em 0.6em', fontSize: '0.72em', fontWeight: 700, color, display: 'flex', justifyContent: 'space-between' }}>
      <span>{name}</span>{win && <span style={{ color: C.green }}>2</span>}
    </Box>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '1.2em', width: '80%', alignItems: 'center' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6em', alignItems: 'center' }}>
        <div style={{ display: 'grid', gap: '0.3em' }}>
          {team('Alpha', C.blue, 0, true)}{team('Delta', C.sub, 0.05)}
          <div style={{ height: '0.4em' }} />
          {team('Bravo', C.orange, 0.1, true)}{team('Echo', C.sub, 0.15)}
        </div>
        <div style={{ display: 'grid', gap: '0.3em' }}>{team('Alpha', C.blue, 0.6, true)}{team('Bravo', C.orange, 0.65)}</div>
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.1, type: 'spring' }}>
          <Box style={{ padding: '0.6em', textAlign: 'center', borderColor: `${C.amber}88` }}>
            <div style={{ fontSize: '1.4em' }}>🏆</div>
            <div style={{ fontWeight: 800, color: C.blue, fontSize: '0.8em' }}>Alpha</div>
          </Box>
        </motion.div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em' }}>
        {chips.map(([label, color], i) => (
          <Pill key={label} color={color} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.4 + i * 0.18, type: 'spring' }}
            style={{ fontSize: '0.85em' }}>{label}</Pill>
        ))}
      </div>
    </div>
  );
}

function Outro() {
  const { t } = useTranslation();
  return (
    <motion.div {...fade} style={{ textAlign: 'center' }}>
      <motion.img src="/logo.png" alt="" initial={{ scale: 0.5, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring' }}
        style={{ width: '3.6em', height: '3.6em', borderRadius: 14 }} />
      <div style={{ fontWeight: 900, fontSize: '2em', marginTop: '0.3em', color: C.text, letterSpacing: '0.06em' }}>FLUX</div>
      <div style={{ color: C.sub, fontSize: '0.95em', marginTop: '0.2em' }}>{t('aboutPage.promo.tagline', 'Run CS2 matches from one panel.')}</div>
    </motion.div>
  );
}

export default function AboutPromo({ capture = false }) {
  const { t } = useTranslation();
  const scenes = [
    { c: SceneSetup, title: t('aboutPage.promo.s1', 'Create the match'), sub: t('aboutPage.promo.s1sub', 'Teams, maps, format and server'), color: C.blue },
    { c: SceneVeto, title: t('aboutPage.promo.s2', 'Veto the maps'), sub: t('aboutPage.promo.s2sub', 'Captains ban and pick, live'), color: C.amber },
    { c: SceneLive, title: t('aboutPage.promo.s3', 'Go live'), sub: t('aboutPage.promo.s3sub', 'Score, killfeed and stats in real time'), color: C.green },
    { c: SceneFix, title: t('aboutPage.promo.s4', 'Fix anything'), sub: t('aboutPage.promo.s4sub', 'Pause, restore a round, keep playing'), color: C.red },
    { c: SceneLan, title: t('aboutPage.promo.s5', 'Run LAN events'), sub: t('aboutPage.promo.s5sub', 'One agent per machine'), color: C.cyan },
    { c: SceneShare, title: t('aboutPage.promo.s6', 'Share everything'), sub: t('aboutPage.promo.s6sub', 'Brackets, demos, Discord and API'), color: C.violet },
  ];
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(capture);
  const [cycle, setCycle] = useState(0);
  const total = scenes.length + 1;

  // Start when scrolled into view; pause when it leaves.
  useEffect(() => {
    if (capture || !ref.current || typeof IntersectionObserver === 'undefined') { setPlaying(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setPlaying(e.isIntersecting), { threshold: 0.35 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [capture]);

  useEffect(() => {
    if (!playing) return undefined;
    const ms = idx < scenes.length ? SCENE_MS : OUTRO_MS;
    const tm = setTimeout(() => {
      if (idx + 1 >= total) { if (capture) return; setCycle((c) => c + 1); setIdx(0); } else setIdx(idx + 1);
    }, ms);
    return () => clearTimeout(tm);
  }, [idx, playing, total, scenes.length, capture]);

  const scene = scenes[idx];
  const Scene = scene && scene.c;
  // Type scales with the promo's own width (container units), not the window's: sized by the window, a narrow
  // column left the scene taller than the space between the progress bar and the caption, and they overlapped.
  return (
    <div style={{ containerType: 'inline-size', width: '100%' }}>
    <div ref={ref} data-promo-done={idx >= total - 1 ? '1' : '0'}
      style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: C.bg, border: capture ? 'none' : `1px solid ${C.border}`,
        borderRadius: capture ? 0 : 12, overflow: 'hidden', color: C.text, fontSize: capture ? 'clamp(14px, 2.4vw, 34px)' : 'clamp(9px, 2.3cqi, 20px)',
        fontFamily: "var(--font-sans, 'Satoshi', sans-serif)" }}>
      {/* subtle grid backdrop */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '2.4em 2.4em' }} />

      {/* progress */}
      <div style={{ position: 'absolute', top: '0.8em', left: '1em', right: '1em', display: 'flex', gap: 4, zIndex: 2 }}>
        {Array.from({ length: total }, (_, i) => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <motion.div key={`${cycle}-${i}-${i === idx}`} style={{ height: '100%', background: i < idx ? C.sub : (scene ? scene.color : C.text) }}
              initial={{ width: i < idx ? '100%' : '0%' }}
              animate={{ width: i < idx ? '100%' : i === idx && playing ? '100%' : '0%' }}
              transition={{ duration: i === idx ? (i < scenes.length ? SCENE_MS : OUTRO_MS) / 1000 : 0, ease: 'linear' }} />
          </div>
        ))}
      </div>

      <div style={{ position: 'absolute', inset: '2.4em 0 5.4em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}> {/* clears the caption block (was 4.2em: tall scenes ran under it) */}
        <AnimatePresence mode="wait">
          <motion.div key={`${cycle}-${idx}`} {...fade} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            {Scene ? <Scene /> : <Outro />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* caption */}
      <div style={{ position: 'absolute', left: '1.2em', right: '1.2em', bottom: '1em', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <AnimatePresence mode="wait">
          {scene && (
            <motion.div key={`${cycle}-cap-${idx}`} {...fade}>
              <div style={{ fontSize: '0.75em', fontWeight: 800, color: scene.color }}>
                {String(idx + 1).padStart(2, '0')} / {String(scenes.length).padStart(2, '0')}
              </div>
              <div style={{ fontWeight: 800, fontSize: '1.15em' }}>{scene.title}</div>
              <div style={{ color: C.sub, fontSize: '0.78em' }}>{scene.sub}</div>
            </motion.div>
          )}
        </AnimatePresence>
        {!capture && (
          <button type="button" onClick={() => { setCycle((c) => c + 1); setIdx(0); setPlaying(true); }}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: C.sub, borderRadius: 8, minHeight: 32, padding: '0.35em 0.8em', fontSize: '0.8em', fontWeight: 700, cursor: 'pointer' }}>
            ↻ {t('aboutPage.promo.replay', 'Replay')}
          </button>
        )}
      </div>
    </div>
    </div>
  );
}
